import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthenticateReloadHandler } from './components/AuthenticateReloadHandler';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { SessionValidator } from './components/SessionValidator';
import AiConnect from './pages/AiConnect';
import AiConsult from './pages/AiConsult';
import AiMetric from './pages/AiMetric';
import AiScribe from './pages/AiScribe';
import Dashboard from './pages/Dashboard';

import { useSelector } from 'react-redux';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import GenerateAssessmentPlan from './pages/GenerateAssessmentPlan';
import PublicAiScribe from './pages/PublicRecording';
import PublicTranscription from './pages/PublicTranscription';
import Settings from './pages/Settings';
import { AuthStateSelector } from './store/selectors';

const queryClient = new QueryClient();

const App = () => {
  const isAuthenticated = useSelector(AuthStateSelector.isAuthenticated);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ThemeProvider defaultTheme="light" storageKey="eyevantage-theme">
            <Toaster />
            <Sonner />
            {/* Session validation - runs automatically when user appears authenticated */}
            <SessionValidator />
            {/* Only mount when authenticated */}
            {isAuthenticated && <AuthenticateReloadHandler />}
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />

                {/* Public pages - no auth required */}
                <Route path="/public-ai-scribe" element={<PublicAiScribe />} />
                <Route path="/public-ai-command" element={<PublicTranscription />} />
                <Route path="/generate-assessment-plan" element={<GenerateAssessmentPlan />} />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-connect"
                  element={
                    <ProtectedRoute>
                      <AiConnect />
                    </ProtectedRoute>
                  }
                />
                {/* Skipped login for now */}
                <Route
                  path="/ai-scribe"
                  element={
                    <ProtectedRoute>
                      <AiScribe />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-metric"
                  element={
                    <ProtectedRoute>
                      <AiMetric />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-consult"
                  element={
                    <ProtectedRoute>
                      <AiConsult />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
